import storageGatewayService from './storageGateway';

/**
 * Two ways to reach Azure Blob storage, behind one interface so the storage
 * browser doesn't branch on which is in use.
 *
 * - SAS backend: the browser talks to Azure directly with an account name and
 *   token the user supplied. What everyone outside the company uses.
 * - Gateway backend: requests go through the storage-gateway service, which
 *   authorizes them and holds the credentials. What employees get, so no
 *   credential capable of deleting anything reaches the browser.
 *
 * Both expose:
 *   list(prefix)          -> [{ name, fullPath, type, size, lastModified }]
 *   readJson(path)        -> parsed JSON
 *   readBlob(path)        -> Blob
 *   writeBlob(path, blob) -> void
 *   remove(path)          -> void
 *   videoUrl(path)        -> URL the video player can fetch ranges from
 */

// Azure's List Blobs API returns at most 5000 entries per response, and may return
// fewer, so a directory is only fully enumerated by following NextMarker.
const LIST_PAGE_SIZE = 5000;
const MAX_LIST_PAGES = 200;

/**
 * Fold one page of List Blobs XML into the accumulating directory listing.
 * Folders are deduplicated across pages via the shared folderSet.
 */
const collectBlobListPage = (xmlDoc, effectivePath, items, folderSet) => {
  const addFolder = (folderName) => {
    if (folderName && !folderSet.has(folderName)) {
      folderSet.add(folderName);
      items.push({ name: folderName, type: 'folder', size: 0, lastModified: null });
    }
  };

  // Folders come back as BlobPrefix entries because we request a delimiter
  for (const prefix of xmlDoc.getElementsByTagName('BlobPrefix')) {
    const name = prefix.getElementsByTagName('Name')[0].textContent;
    const relativePath = effectivePath && name.startsWith(effectivePath)
      ? name.substring(effectivePath.length)
      : name;

    addFolder(
      relativePath.endsWith('/')
        ? relativePath.slice(0, -1).split('/').pop()
        : relativePath.split('/')[0]
    );
  }

  for (const blob of xmlDoc.getElementsByTagName('Blob')) {
    const name = blob.getElementsByTagName('Name')[0].textContent;

    let isInCurrentDir = false;
    let relativeName = name;

    if (!effectivePath) {
      if (name.includes('/')) {
        // Nested blob seen at the root: surface its top-level folder instead
        addFolder(name.split('/')[0]);
      } else {
        isInCurrentDir = true;
      }
    } else if (name.startsWith(effectivePath)) {
      relativeName = name.substring(effectivePath.length);
      isInCurrentDir = !relativeName.includes('/');
    }

    if (!isInCurrentDir || !relativeName) continue;

    const properties = blob.getElementsByTagName('Properties')[0];
    const lastModifiedElement = properties?.getElementsByTagName('Last-Modified')[0];

    items.push({
      name: relativeName,
      fullPath: name,
      type: 'file',
      size: properties ? parseInt(properties.getElementsByTagName('Content-Length')[0].textContent) : 0,
      lastModified: lastModifiedElement ? new Date(lastModifiedElement.textContent) : null,
    });
  }
};

export const createSasBackend = ({ storageAccount, sasToken, container }) => {
  const blobUrl = (path) =>
    `https://${storageAccount}.blob.core.windows.net/${container}/${path}?${sasToken}`;

  const listUrl = (prefix, marker) => {
    const base = `https://${storageAccount}.blob.core.windows.net/${container}`;
    let url = `${base}?${sasToken}&restype=container&comp=list&delimiter=/&maxresults=${LIST_PAGE_SIZE}`;
    if (prefix) url += `&prefix=${encodeURIComponent(prefix)}`;
    if (marker) url += `&marker=${encodeURIComponent(marker)}`;
    return url;
  };

  const ensureOk = (response, action) => {
    if (!response.ok) {
      throw new Error(`${action} failed: ${response.status} ${response.statusText}`);
    }
    return response;
  };

  return {
    async list(prefix = '') {
      const parser = new DOMParser();
      const items = [];
      const folderSet = new Set();
      let marker = '';

      for (let page = 0; page < MAX_LIST_PAGES; page++) {
        const response = await fetch(listUrl(prefix, marker));
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        const xmlDoc = parser.parseFromString(await response.text(), 'text/xml');
        collectBlobListPage(xmlDoc, prefix, items, folderSet);

        const nextMarkerElement = xmlDoc.getElementsByTagName('NextMarker')[0];
        const nextMarker = nextMarkerElement ? nextMarkerElement.textContent.trim() : '';
        if (!nextMarker || nextMarker === marker) break;
        marker = nextMarker;
      }

      return items;
    },

    async readJson(path) {
      return ensureOk(await fetch(blobUrl(path)), 'Download').json();
    },

    async readBlob(path) {
      return ensureOk(await fetch(blobUrl(path)), 'Download').blob();
    },

    async writeBlob(path, blob) {
      ensureOk(
        await fetch(blobUrl(path), {
          method: 'PUT',
          body: blob,
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json' },
        }),
        'Upload'
      );
    },

    async remove(path) {
      ensureOk(await fetch(blobUrl(path), { method: 'DELETE' }), 'Delete');
    },

    async videoUrl(path) {
      return blobUrl(path);
    },
  };
};

export const createGatewayBackend = ({ container }) => ({
  // The gateway enumerates the folder server-side, so there is no paging here
  list: (prefix = '') => storageGatewayService.list(container, prefix),
  readJson: (path) => storageGatewayService.readJson(container, path),
  readBlob: (path) => storageGatewayService.readBlob(container, path),
  writeBlob: (path, blob) => storageGatewayService.writeBlob(container, path, blob),
  remove: (path) => storageGatewayService.deleteBlob(container, path),
  videoUrl: (path) => storageGatewayService.videoUrl(container, path),
});
